import { cacheKeys } from "@constants/cache-keys";
import { isScreenshotMode } from "@constants/common";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { getProgressCardsData } from "@helpers/progress-data";
import useMixedItemDist from "@hooks/useMixedItemDist";
import type { ProgressCardsData } from "@memorang/ui/components/ProgressApplet/types";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fetchUserSessions } from "../api/queries";
import { MOCK_LEGACY_USER_SESSIONS } from "../constants/legacy-sessions";
import { MOCK_PROGRESS_CARDS } from "../constants/mocked-progress-cards";
import { getLegacyTestsCardsData } from "../helpers/legacy-tests-data";
import { useHasAccess } from "../hooks/useHasAccess";

export const useProgressCards = () => {
	const { data: commonDistribution, isLoading } = useMixedItemDist();

	const { userPublicMetadataId } = useAuthContext();
	const { t } = useTranslation();
	const theme = useAppTheme();
	const { hasStudyPackContentAccess } = useHasAccess();
	const {
		currentExamContext: { legacySessionsCollectionId },
	} = useExamContext();

	const legacySessionsQuery = useQuery({
		queryKey: [
			cacheKeys.userSessions,
			userPublicMetadataId,
			legacySessionsCollectionId,
			"COMPLETE",
		],
		queryFn: () => {
			if (isScreenshotMode) {
				return MOCK_LEGACY_USER_SESSIONS;
			}

			return fetchUserSessions({
				userId: userPublicMetadataId!,
				collectionId: legacySessionsCollectionId!,
				status: "COMPLETE",
			});
		},
		enabled:
			Boolean(userPublicMetadataId) &&
			Boolean(legacySessionsCollectionId) &&
			!isScreenshotMode,
		select: getLegacyTestsCardsData,
	});

	if (isScreenshotMode) {
		const emptyWeakTagIds: string[] = [];
		return {
			data: MOCK_PROGRESS_CARDS,
			isLoading: false,
			isEnabled: true,
			needsImprovementProgressData: null,
			weakTagIds: emptyWeakTagIds,
			showEmptyState: false,
		};
	}

	const generic = commonDistribution
		? getProgressCardsData(
				commonDistribution,
				"Progress",
				hasStudyPackContentAccess,
				t,
				theme,
			)
		: null;

	const masteryTags = generic?.masteryTagsProgressData ?? [];

	const needsImprovementProgressData =
		generic?.needsImprovementProgressData ?? null;

	const data: ProgressCardsData = {
		needsImprovement: needsImprovementProgressData?.items ?? [],
		needsImprovementTagType: masteryTags[0]?.tagType,
		masteryTags,
		practiceTests: generic?.practiceTestsCardsData,
		legacyTests: legacySessionsQuery.data,
		summary: generic?.progressPageSummaryCardData ?? undefined,
	};

	const weakTagIds =
		needsImprovementProgressData?.items.map((i) => String(i.id!)) ?? [];

	return {
		data,
		isLoading,
		isEnabled: masteryTags.length > 0,
		needsImprovementProgressData,
		weakTagIds,
		showEmptyState: generic?.showEmptyState ?? false,
	};
};
